import { useState, useEffect } from "react";
import { Search, Plus, MessageCircle, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { AddCustomerDialog } from "./AddCustomerDialog";
import { CustomerDetailsSheet } from "./CustomerDetailsSheet";
import { useTranslation } from "react-i18next";

interface Customer {
  phone_number: string;
  name: string;
  email: string | null;
  last_request_date: string;
}

// Custom WhatsApp and Telegram icons
const MessagingIcon = ({ platform }: { platform: string }) => {
  if (platform === 'telegram') {
    return (
      <img 
        src="/icons8-telegram.svg" 
        alt="Telegram" 
        className="w-4 h-4" 
      />
    );
  } else {
    return (
      <img 
        src="/icons8-whatsapp.svg" 
        alt="WhatsApp" 
        className="w-4 h-4" 
      />
    );
  }
};

const CustomersView = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isCustomerDetailsOpen, setIsCustomerDetailsOpen] = useState(false);
  const [preferredMessaging, setPreferredMessaging] = useState("whatsapp");
  const { toast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    fetchCustomers();
    fetchUserPreferences();
  }, []);

  const fetchUserPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('preferred_messaging')
        .eq('id', user.id)
        .single();

      if (profile?.preferred_messaging) {
        setPreferredMessaging(profile.preferred_messaging);
      }
    } catch (error) {
      console.error('Error fetching user preferences:', error);
    }
  };

  const fetchCustomers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Verify the user is authenticated
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        console.error('Authentication error:', authError);
        setError(`Authentication error: ${authError.message}`);
        throw authError;
      }
      
      if (!user) {
        setError("User not authenticated");
        throw new Error("User not authenticated");
      }

      console.log("Current user ID:", user.id);

      // Fetch customers - with RLS policies in place
      // We don't need to filter by user_id in the query as RLS will handle this
      const { data, error: fetchError } = await supabase
        .from('customers')
        .select('*')
        .order('last_request_date', { ascending: false });
      
      if (fetchError) {
        console.error('Database error:', fetchError);
        setError(`Database error: ${fetchError.message}`);
        throw fetchError;
      }

      console.log('Fetched customers:', data);
      
      if (!data || data.length === 0) {
        console.log("No customers found for this user");
      }

      setCustomers(data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setError(error instanceof Error ? error.message : "Unknown error");
      toast({
        variant: "destructive",
        title: "Error",
        description: t('customers.failedToFetch'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerAdded = async () => {
    await fetchCustomers();
    setIsAddCustomerOpen(false);
  };

  const handleViewCustomerDetails = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsCustomerDetailsOpen(true);
  };

  const handleContactCustomer = (e: React.MouseEvent, customer: Customer) => {
    e.stopPropagation(); // Prevent triggering the card click
    
    // Format phone number (ensure it starts with country code and has no special characters)
    let phoneNumber = customer.phone_number.replace(/\s/g, '');
    
    // Ensure it has a + if needed
    if (!phoneNumber.startsWith('+')) {
      // Add default country code (e.g., +254 for Kenya) if missing
      phoneNumber = '+' + phoneNumber;
    }
    
    // Remove the + for the URL
    const formattedNumber = phoneNumber.replace(/^\+/, '');
    
    if (preferredMessaging === 'telegram') {
      // For Telegram, we need to use a universal link that works with the app
      // This will open the Telegram app if installed, or web version as fallback
      window.open(`https://telegram.me/+${formattedNumber}`, '_blank', 'noopener,noreferrer');
    } else {
      // For WhatsApp, use the standard API with formatted number
      window.open(`https://api.whatsapp.com/send?phone=${formattedNumber}`, '_blank', 'noopener,noreferrer');
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.phone_number.includes(searchQuery) ||
    (customer.email && customer.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <img 
          src="/lovable-uploads/f8714beb-a432-4da6-acab-3ef6f0a42367.png" 
          alt="Gebeya Logo" 
          className="h-12 w-12 gebeya-spinner" 
        />
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-end mb-8">
        <Button
          onClick={() => setIsAddCustomerOpen(true)}
          className="bg-gradient-to-r from-gebeya-pink to-gebeya-orange text-white hover:opacity-90 transition-opacity w-full sm:w-auto"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('customers.addCustomer')}
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-100">
        <div className="p-4 border-b">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 w-full">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <Input
                  type="text"
                  placeholder={t('customers.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 focus:ring-gebeya-pink focus:border-gebeya-pink"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {filteredCustomers.length > 0 ? (
            filteredCustomers.map((customer) => (
              <div
                key={customer.phone_number}
                className="p-4 hover:bg-gray-50/80 cursor-pointer"
                onClick={() => handleViewCustomerDetails(customer)}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{customer.name}</h3>
                    <div className="text-sm text-gray-500">
                      <p>{t('customers.phone')}: {customer.phone_number}</p>
                      {customer.email && <p>{t('customers.email')}: {customer.email}</p>}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500 my-2 md:my-0 md:mr-4">
                    {t('customers.lastRequest')}: {customer.last_request_date ? format(new Date(customer.last_request_date), "PPP") : t('customers.noRequestsYet')}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-1 hover:bg-gray-100 w-full md:w-auto"
                    onClick={(e) => handleContactCustomer(e, customer)}
                  >
                    {preferredMessaging === 'telegram' ? (
                      <>
                        <MessagingIcon platform="telegram" />
                        <span>{t('customers.telegram')}</span>
                      </>
                    ) : (
                      <>
                        <MessagingIcon platform="whatsapp" />
                        <span>{t('customers.whatsapp')}</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">
                {searchQuery ? t('customers.noCustomersMatch') : t('customers.noCustomersYet')}
              </p>
              <p className="text-sm text-gray-400 mt-2">
                {t('customers.tryAddingCustomer')}
              </p>
            </div>
          )}
        </div>
      </div>

      <AddCustomerDialog
        isOpen={isAddCustomerOpen}
        onClose={() => setIsAddCustomerOpen(false)}
        onSuccess={handleCustomerAdded}
      />

      <CustomerDetailsSheet
        customer={selectedCustomer}
        isOpen={isCustomerDetailsOpen}
        onClose={() => setIsCustomerDetailsOpen(false)}
      />
    </>
  );
};

export default CustomersView;
